import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';

import {
    CompoundOrigin,
    CompoundTranslationTypeGroupKey,
    RemovalReasonTexts
} from 'src/app/contracts';

@Injectable()
export class LocationDetailsHelper {
    constructor(
        @Inject(LOCALE_ID) public locale,
    ) {

    }
    public getRemovalReason(removalEnum): string {
        const reason = RemovalReasonTexts.get(removalEnum);
        return reason ? reason : '-';
    }

    public getNumberFormat(val: number): string {
        const n = this.getValidNumber(val);
        let digitsInfo = '';
        if (n >= 10.0) { // xx.x
            digitsInfo = '1.1-1';
        } else if (n >= 1.0) { // x.xx
            digitsInfo = '1.2-2';
        } else if (n >= 0.1) {// 0.xxx
            digitsInfo = '1.3-3';
        } else if (n >= 0.01) { // 0.0xxx
            digitsInfo = '1.4-4';
        } else { // 0.00xxx
            digitsInfo = '1.5-5';
        }

        const result = new DecimalPipe(this.locale).transform(n, digitsInfo);
        return result;
    }

    public getNumberFormatWithDigitsInfo(val: number, digitsInfo: string): string {
        const n = this.getValidNumber(val);
        const result = new DecimalPipe(this.locale).transform(n, digitsInfo);
        return result;
    }

    public getValidNumber(val: number): number {
        const result = (!val || isNaN(val) || !Number.isFinite(val)) ? 0 : val;
        return result;
    }

    public getQualityCriterium(gvk): string {
        if (gvk > 0) {
            return this.getNumberFormat(gvk);
        } else { // Assume gvk 0 = no criterium
            return '-';
        }
    }

    public getFactor(factor, gvk, conc): string {
        if (gvk === 0) { // Assume gvk 0 = no criterium
            return '-';
        }
        if (conc === 0) {
            return '0';
        } else {
            return this.getNumberFormat(factor);
        }
    }

    public getDateFormat(val: Date): string {
        const result = new DatePipe(this.locale).transform(val);
        return result;
    }

    public getScreengResultTableHeader(type: string): Array<string> {
        const results = ['Dato'];
        const tableHeaders = [
            'Stofkilde',
            'Kvalitetskriterie',
            'Worst case - Koncentration',
            '100m nedstrøms - Koncentration',
            'Årsag til frasortering',
            'Faktor',
            'Flag'
        ];
        if (type === CompoundTranslationTypeGroupKey.Brancher){
            results.push('Branche');
            results.push('Kildestørrelse');
            results.push('Stofnavn');
        } else if (type === CompoundTranslationTypeGroupKey.Aktiviteter) {
            results.push('Aktiviteter');
            results.push('Kildestørrelse');
            results.push('Stofnavn');
        } else {
            results.push('Stofnavn');
            results.push('Kildestørrelse');
        }
        return results.concat(tableHeaders);
    }

    public getLeftBoxTitle(pollutionStatusCode: string): string {
        switch (pollutionStatusCode) {
            case '07':
                return 'V1 kortlagt lokalitet';
            case '08':
                return 'V2 kortlagt lokalitet';
            case '13':
                return 'V1 og V2 kortlagt lokalitet';
            default:
                return '-';
        }
    }

    public getCompoundOriginText(origin: number): string {
        switch (origin) {
            case CompoundOrigin.Dkjord:
                return 'Dkjord';
            case CompoundOrigin.Landfill:
                return 'Losseplads';
            default:
                return '-';
        }
    }

    public getRow(type: string, result: any, exportType: string): any {
        const factor = exportType === 'CSV'
            ? this.getFactor(result.factor, result.qualityCriterion, result.concentrationDownstream)
            : {
                text: this.getFactor(result.factor, result.qualityCriterion, result.concentrationDownstream),
                bold: true,
                color: result.factor > 1 ? '#b1114b' : ''
              };
        const results = [this.getDateFormat(result.createdAt)];
        let columnContents: any[];
        columnContents = [
            this.getCompoundOriginText(result.compoundOrigin),
            this.getQualityCriterium(result.qualityCriterion),
            `${this.getNumberFormat(result.worstCaseConcentration)} μg/L`,
            `${this.getNumberFormat(result.concentrationDownstream)} μg/L`,
            `${this.getRemovalReason(result.removalReason)}`,
            factor,
            result.flag > 0 ? 'ja' : 'nej'
        ];

        if (type === CompoundTranslationTypeGroupKey.Stoffer) {
            results.push(result.compoundName); // Stofnavn
            results.push(this.getNumberFormat(result.sourceSize)); // Kildestørrelse
        } else if (type === CompoundTranslationTypeGroupKey.Brancher){
            results.push(result.industry?.name ?? '-'); // branche
            results.push(this.getNumberFormat(result.sourceSize)); // Kildestørrelse
            results.push(result.compoundName); // Stofnavn
        } else {
            results.push(result.activity?.name ?? '-'); // Aktiviteter
            results.push(this.getNumberFormat(result.sourceSize)); // Kildestørrelse
            results.push(result.compoundName); // Stofnavn
        }
        return results.concat(columnContents);
    }
}
